import { OrdersState } from 'app/page/orders/redux/model/orders-state';
import { OrdersActions } from 'app/page/orders/redux/model/orders-actions';
import { OrdersActionType } from 'app/page/orders/redux/model/orders-action-type';
import { PendingActionUtils } from 'app/page/orders/utils/pending-action-utils';
import { PendingType } from 'app/page/orders/redux/model/pending-type';
import { ReducerUtils } from 'app/page/orders/utils/reducer-utils';
import { OrderUtils } from 'app/page/orders/utils/order-utils';

const initialOrdersState: OrdersState = {
    initialized: false,
    billingAccounts: [],
    pendingActions: [],
    availableTime: [],
};

const ordersReducer = (state: OrdersState = initialOrdersState, action: OrdersActions): OrdersState => {
    switch (action.type) {
        case OrdersActionType.INITIALIZE_BILLING_ACCOUNTS_START:
        case OrdersActionType.INITIALIZE_BILLING_ACCOUNTS_FAILURE:
            return {
                ...state,
                initialized: false,
            };
        case OrdersActionType.INITIALIZE_BILLING_ACCOUNTS_SUCCESS:
            return {
                ...state,
                initialized: true,
            };
        case OrdersActionType.LOAD_BILLING_ACCOUNTS_START:
            return {
                ...state,
                billingAccounts: [],
                selectedBillingAccountId: undefined,
                pendingActions: PendingActionUtils.withStartedPendingAction(
                    PendingType.LOAD_BILLING_ACCOUNTS,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.LOAD_BILLING_ACCOUNTS_SUCCESS: {
            return {
                ...state,
                billingAccounts: action.billingAccounts,
                pendingActions: PendingActionUtils.withRemovedPendingAction(
                    PendingType.LOAD_BILLING_ACCOUNTS,
                    state.pendingActions,
                ),
            };
        }
        case OrdersActionType.LOAD_BILLING_ACCOUNTS_FAILURE: {
            return {
                ...state,
                pendingActions: PendingActionUtils.withPendingActionFailure(
                    {
                        type: PendingType.LOAD_BILLING_ACCOUNTS,
                        reason: action.reason,
                    },
                    state.pendingActions,
                ),
            };
        }
        case OrdersActionType.LOAD_CUSTOMER_INFORMATION_START:
            return {
                ...state,
                pendingActions: PendingActionUtils.withStartedPendingAction(
                    PendingType.LOAD_CUSTOMER_INFORMATION,
                    state.pendingActions,
                    action.billingAccountId,
                ),
            };
        case OrdersActionType.LOAD_CUSTOMER_INFORMATION_SUCCESS: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateBillingAccounts(
                    state.billingAccounts,
                    action.customerInformation.billingAccountId,
                    {
                        customerInformation: action.customerInformation,
                    },
                ),
                pendingActions: PendingActionUtils.withRemovedPendingAction(
                    PendingType.LOAD_CUSTOMER_INFORMATION,
                    state.pendingActions,
                    action.customerInformation.billingAccountId,
                ),
            };
        }
        case OrdersActionType.LOAD_CUSTOMER_INFORMATION_FAILURE: {
            return {
                ...state,
                pendingActions: PendingActionUtils.withPendingActionFailure(
                    {
                        type: PendingType.LOAD_CUSTOMER_INFORMATION,
                        reason: action.reason,
                        id: action.billingAccountId,
                    },
                    state.pendingActions,
                ),
            };
        }
        case OrdersActionType.SET_SELECTED_BILLING_ACCOUNT_ID: {
            return {
                ...state,
                selectedBillingAccountId: action.selectedBillingAccountId,
            };
        }
        case OrdersActionType.INITIALIZE_ORDERS_START:
        case OrdersActionType.INITIALIZE_ORDERS_FAILURE:
            return {
                ...state,
                billingAccounts: ReducerUtils.updateBillingAccounts(state.billingAccounts, action.billingAccountId, {
                    initialized: false,
                }),
            };
        case OrdersActionType.INITIALIZE_ORDERS_SUCCESS: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateBillingAccounts(state.billingAccounts, action.billingAccountId, {
                    initialized: true,
                }),
            };
        }
        case OrdersActionType.LOAD_INFLIGHT_ORDERS_START:
            return {
                ...state,
                pendingActions: PendingActionUtils.withStartedPendingAction(
                    PendingType.LOAD_INFLIGHT_ORDERS,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.LOAD_INFLIGHT_ORDERS_SUCCESS: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateBillingAccounts(state.billingAccounts, action.billingAccountId, {
                    activeOrders: action.activeOrders,
                }),
                pendingActions: PendingActionUtils.withRemovedPendingAction(
                    PendingType.LOAD_INFLIGHT_ORDERS,
                    state.pendingActions,
                ),
            };
        }
        case OrdersActionType.LOAD_INFLIGHT_ORDERS_FAILURE: {
            return {
                ...state,
                pendingActions: PendingActionUtils.withPendingActionFailure(
                    {
                        type: PendingType.LOAD_INFLIGHT_ORDERS,
                        reason: action.reason,
                    },
                    state.pendingActions,
                ),
            };
        }
        case OrdersActionType.SET_SELECTED_ORDER_ID: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateBillingAccounts(state.billingAccounts, action.billingAccountId, {
                    selectedOrderId: action.selectedOrderId,
                }),
            };
        }
        case OrdersActionType.CREATE_ORDER_SUMMARY_START:
        case OrdersActionType.UPDATE_SHOPPING_CART_START: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateActiveOrder(
                    state.billingAccounts,
                    action.billingAccountId,
                    action.orderId,
                    {
                        pendingUpdate: true,
                    },
                ),
            };
        }
        case OrdersActionType.CREATE_ORDER_SUMMARY_SUCCESS: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateActiveOrder(
                    state.billingAccounts,
                    action.billingAccountId,
                    action.orderId,
                    {
                        orderSummary: action.orderSummary,
                        initialized: true,
                        pendingUpdate: false,
                    },
                ),
            };
        }
        case OrdersActionType.CREATE_ORDER_SUMMARY_FAILURE:
        case OrdersActionType.UPDATE_SHOPPING_CART_FAILURE: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateActiveOrder(
                    state.billingAccounts,
                    action.billingAccountId,
                    action.orderId,
                    {
                        pendingUpdate: false,
                        failureReason: action.reason,
                    },
                ),
            };
        }
        case OrdersActionType.INITIALIZE_SCHEDULE_APPOINTMENT_START:
            return {
                ...state,
                pendingActions: PendingActionUtils.withStartedPendingAction(
                    PendingType.INITIALIZE_SCHEDULE_APPOINTMENT,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.INITIALIZE_SCHEDULE_APPOINTMENT_SUCCESS:
            return {
                ...state,
                billingAccounts: ReducerUtils.updateAppointment(
                    state.billingAccounts,
                    action.billingAccountId,
                    action.orderId,
                    action.appointment,
                ),
                availableTime: action.availableTime,
                pendingActions: PendingActionUtils.withRemovedPendingAction(
                    PendingType.INITIALIZE_SCHEDULE_APPOINTMENT,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.INITIALIZE_SCHEDULE_APPOINTMENT_FAILURE:
            return {
                ...state,
                pendingActions: PendingActionUtils.withPendingActionFailure(
                    {
                        type: PendingType.INITIALIZE_SCHEDULE_APPOINTMENT,
                        reason: action.reason,
                    },
                    state.pendingActions,
                ),
            };
        case OrdersActionType.FETCH_AVAILABLE_TIME_START:
            return {
                ...state,
                pendingActions: PendingActionUtils.withStartedPendingAction(
                    PendingType.FETCH_AVAILABLE_TIME,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.FETCH_AVAILABLE_TIME_SUCCESS:
            return {
                ...state,
                availableTime: action.availableTime,
                pendingActions: PendingActionUtils.withRemovedPendingAction(
                    PendingType.FETCH_AVAILABLE_TIME,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.SUBMIT_SCHEDULE_APPOINTMENT_START:
            return {
                ...state,
                pendingActions: PendingActionUtils.withStartedPendingAction(
                    PendingType.SUBMIT_SCHEDULE_APPOINTMENT,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.SUBMIT_SCHEDULE_APPOINTMENT_SUCCESS:
            return {
                ...state,
                billingAccounts: ReducerUtils.updateAppointment(
                    state.billingAccounts,
                    action.billingAccountId,
                    action.orderId,
                    action.appointment,
                ),
                pendingActions: PendingActionUtils.withRemovedPendingAction(
                    PendingType.SUBMIT_SCHEDULE_APPOINTMENT,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.SUBMIT_SCHEDULE_APPOINTMENT_FAILURE:
            return {
                ...state,
                pendingActions: PendingActionUtils.withPendingActionFailure(
                    {
                        type: PendingType.SUBMIT_SCHEDULE_APPOINTMENT,
                        reason: action.reason,
                    },
                    state.pendingActions,
                ),
            };
        case OrdersActionType.UPDATE_ACTIVATION_DATE_START:
            return {
                ...state,
                pendingActions: PendingActionUtils.withStartedPendingAction(
                    PendingType.UPDATE_ACTIVATION_DATE,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.UPDATE_ACTIVATION_DATE_SUCCESS:
            return {
                ...state,
                pendingActions: PendingActionUtils.withRemovedPendingAction(
                    PendingType.UPDATE_ACTIVATION_DATE,
                    state.pendingActions,
                ),
            };
        case OrdersActionType.UPDATE_ACTIVATION_DATE_FAILURE:
            return {
                ...state,
                pendingActions: PendingActionUtils.withPendingActionFailure(
                    {
                        type: PendingType.UPDATE_ACTIVATION_DATE,
                        reason: action.reason,
                    },
                    state.pendingActions,
                ),
            };
        case OrdersActionType.UPDATE_SHOPPING_CART_SUCCESS: {
            return {
                ...state,
                billingAccounts: ReducerUtils.updateActiveOrder(
                    state.billingAccounts,
                    action.billingAccountId,
                    action.orderId,
                    {
                        orderSummary: {
                            ...OrderUtils.getOrderSummary(
                                state.billingAccounts,
                                action.billingAccountId,
                                action.orderId,
                            ),
                            shoppingCart: action.shoppingCart,
                        },
                        pendingUpdate: false,
                    },
                ),
            };
        }
        case OrdersActionType.ORDER_RESET: {
            return initialOrdersState;
        }
        default:
            return state;
    }
};

export { ordersReducer };
